import {
  useState,
  type ComponentType,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  type SVGProps,
} from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  IconChevronDown,
  IconChevronRightSm,
  IconHomeOutline20,
  IconHomeFilled,
  IconLogoMark,
  IconMap,
  IconMap20,
  IconNavSearch,
  IconSidebarToggle,
} from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { PRODUCT_ICON } from '@/packages/explore/components/product-icons'
import { PRODUCT_GROUPS } from '@/packages/explore/data/explore.mock'
import { useLocalize } from '@/packages/explore/lib/localize'
import type { Product, ProductGroup } from '@/packages/explore/types'

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>

const DISCOVER_PATH = '/discover'

const HOME_CATEGORY = 'forYou'

const VISIBLE = 3

const SIDEBAR_GROUPS: { id: string; pinned: string[] }[] = [
  {
    id: 'student-services',
    pinned: ['ss-do-it-online', 'programmes-curriculum', 'academic-calendar'],
  },
  { id: 'student-life', pinned: ['mobilities', 'sport', 'sl-news'] },
  { id: 'community', pinned: ['student-associations', 'mentor-programmes', 'advanced-college'] },
]

const PRODUCT_ROUTES: Record<string, string> = {
  'sl-news': `${DISCOVER_PATH}?category=news`,
  'sl-events': `${DISCOVER_PATH}?category=events`,
  scholarships: `${DISCOVER_PATH}?category=scholarships`,
}

function hasActiveCategory(search: string): boolean {
  const category = new URLSearchParams(search).get('category')
  return category !== null && category !== HOME_CATEGORY
}

function ProductIcon({
  name,
  active = false,
}: {
  name?: Product['icon']
  active?: boolean
}): ReactElement {
  const Glyph = name ? PRODUCT_ICON[name] : undefined

  if (!Glyph) return <span className="size-5 shrink-0" aria-hidden="true" />

  return (
    <Glyph
      className={cn('size-5 shrink-0', active ? 'text-sidebar-icon-selected' : 'text-sidebar-icon')}
      aria-hidden="true"
    />
  )
}

function orderProducts(group: ProductGroup, pinned: string[]): Product[] {
  const first = pinned.flatMap((id) => group.products.find((product) => product.id === id) ?? [])
  const rest = group.products.filter((product) => !pinned.includes(product.id))
  return [...first, ...rest]
}

function useIsRouteActive(to: string | undefined): boolean {
  const { pathname, search } = useLocation()
  if (!to) return false

  const [path = '', query = ''] = to.split('?')
  const wanted = new URLSearchParams(query)

  if (wanted.size === 0) {
    return pathname === path || pathname.startsWith(`${path}/`)
  }
  if (pathname !== path) return false

  const current = new URLSearchParams(search)
  for (const [key, value] of wanted) {
    if (current.get(key) !== value) return false
  }
  return true
}

function isTruncated(element: HTMLElement): boolean {
  if (element.scrollWidth > element.clientWidth) return true

  const range = document.createRange()
  range.selectNodeContents(element)
  return range.getBoundingClientRect().width > element.getBoundingClientRect().width
}

function useSidebarTooltip(label: string): {
  triggerProps: {
    onMouseEnter: (event: MouseEvent<HTMLElement>) => void
    onMouseLeave: () => void
    onFocus: (event: FocusEvent<HTMLElement>) => void
    onBlur: () => void
  }
  hide: () => void
  tooltip: ReactElement | null
} {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const show = (element: HTMLElement): void => {
    const text = element.querySelector<HTMLElement>('[data-sidebar-label]')
    if (text && !isTruncated(text)) return

    const rect = element.getBoundingClientRect()

    const navRight = element.closest('nav')?.getBoundingClientRect().right ?? rect.right
    setPos({ top: rect.top + rect.height / 2, left: navRight + 8 })
  }
  const hide = (): void => {
    setPos(null)
  }

  const tooltip = pos
    ? createPortal(
        <div
          role="tooltip"
          style={{ top: pos.top, left: pos.left }}
          className="hub-label-md pointer-events-none fixed z-100 -translate-y-1/2 rounded-[6px] bg-[#1b1b1f] px-3 py-2 whitespace-nowrap text-white shadow-lg dark:bg-[#ececf1] dark:text-[#1b1b1f] dark:shadow-black/40"
        >
          {label}
        </div>,
        document.body,
      )
    : null

  return {
    triggerProps: {
      onMouseEnter: (event) => {
        show(event.currentTarget)
      },
      onMouseLeave: hide,
      onFocus: (event) => {
        show(event.currentTarget)
      },
      onBlur: hide,
    },
    hide,
    tooltip,
  }
}

interface NavItem {
  to: string
  end?: boolean
  label: string
  Icon: IconComponent
  ActiveIcon?: IconComponent
}

interface SidebarAction {
  label: string
  Icon: IconComponent
  onClick: () => void
  isActive?: boolean
}

type FixEntry = { kind: 'link'; item: NavItem } | { kind: 'action'; action: SidebarAction }

const rowClassName = (isActive = false): string =>
  cn(
    'flex w-full cursor-pointer items-center gap-2 rounded-xs p-2 transition-colors',
    isActive ? 'bg-sidebar-selected' : 'hover:bg-sidebar-selected',
  )

function SidebarLink({
  item,
  onNavigate,
  forceInactive,
}: {
  item: NavItem
  onNavigate?: () => void
  forceInactive?: boolean
}): ReactElement {
  const { search } = useLocation()

  const excluded = (item.to === DISCOVER_PATH && hasActiveCategory(search)) || forceInactive

  return (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      onClick={onNavigate}
      className={({ isActive }) => rowClassName(isActive && !excluded)}
    >
      {({ isActive: linkActive }) => {
        const isActive = linkActive && !excluded
        const Glyph = isActive ? (item.ActiveIcon ?? item.Icon) : item.Icon
        return (
          <>
            <Glyph
              className={cn(
                'size-5 shrink-0',
                isActive ? 'text-sidebar-icon-selected' : 'text-sidebar-icon',
              )}
              aria-hidden="true"
            />
            <span
              className={cn(
                'text-sidebar-text min-w-px flex-1 truncate',
                isActive ? 'hub-label-md-highlight' : 'hub-label-md',
              )}
            >
              {item.label}
            </span>
          </>
        )
      }}
    </NavLink>
  )
}

function ProductContent({
  product,
  label,
  active,
}: {
  product: Product
  label: string
  active: boolean
}): ReactElement {
  return (
    <>
      <ProductIcon name={product.icon} active={active} />
      <span
        data-sidebar-label
        className={cn(
          'text-sidebar-text min-w-px flex-1 truncate',
          active ? 'hub-label-md-highlight' : 'hub-label-md',
        )}
      >
        {label}
      </span>
    </>
  )
}

function SidebarProductLink({
  product,
  onNavigate,
  forceInactive,
}: {
  product: Product
  onNavigate?: () => void
  forceInactive?: boolean
}): ReactElement {
  const localize = useLocalize()
  const label = localize(product.label)
  const { triggerProps, hide, tooltip } = useSidebarTooltip(label)

  const to = PRODUCT_ROUTES[product.id] ?? product.to
  const isActiveRoute = useIsRouteActive(to)
  const isActive = isActiveRoute && !forceInactive

  const handleClick = (): void => {
    hide()
    onNavigate?.()
  }

  if (to) {
    return (
      <>
        <Link
          to={to}
          onClick={handleClick}
          aria-current={isActive ? 'page' : undefined}
          className={rowClassName(isActive)}
          {...triggerProps}
        >
          <ProductContent product={product} label={label} active={isActive} />
        </Link>
        {tooltip}
      </>
    )
  }

  return (
    <>
      <a
        href={product.href ? localize(product.href) : undefined}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className={rowClassName()}
        {...triggerProps}
      >
        <ProductContent product={product} label={label} active={false} />
      </a>
      {tooltip}
    </>
  )
}

function ShowMoreRow({
  expanded,
  onToggle,
  label,
}: {
  expanded: boolean
  onToggle: () => void
  label: string
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="hover:bg-sidebar-selected flex w-full cursor-pointer items-center gap-2 rounded-xs p-2 transition-colors"
    >
      <IconChevronDown
        className={cn(
          'text-sidebar-icon size-5 shrink-0 transition-transform',
          expanded && 'rotate-180',
        )}
        aria-hidden="true"
      />
      <span className="hub-label-md text-sidebar-text min-w-px flex-1 truncate text-left">
        {label}
      </span>
    </button>
  )
}

function GroupLabel({
  label,
  open,
  onToggle,
}: {
  label: string
  open: boolean
  onToggle: () => void
}): ReactElement {
  const { triggerProps, hide, tooltip } = useSidebarTooltip(label)

  return (
    <>
      <button
        type="button"
        onClick={() => {
          hide()
          onToggle()
        }}
        aria-expanded={open}
        className="group flex h-5 w-full cursor-pointer items-center justify-start px-2"
        {...triggerProps}
      >
        <span
          data-sidebar-label
          className="hub-meta-lg text-sidebar-text-secondary min-w-px truncate text-left"
        >
          {label}
        </span>
        <IconChevronRightSm
          className={cn(
            'text-sidebar-icon size-5 shrink-0 transition-[transform,opacity]',
            open && 'rotate-90 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
          aria-hidden="true"
        />
      </button>
      {tooltip}
    </>
  )
}

function ClosedSidebarToggle({ onToggle }: { onToggle: () => void }) {
  const { t } = useTranslation()
  const { triggerProps, hide, tooltip } = useSidebarTooltip(t('nav.openMenu'))

  return (
    <>
      <button
        type="button"
        onClick={() => {
          hide()
          onToggle()
        }}
        aria-label={t('nav.openMenu')}
        className="group text-icon hover:bg-sidebar-selected focus-visible:bg-sidebar-selected flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-[8px] p-2 transition-colors"
        {...triggerProps}
      >
        <IconLogoMark
          className="size-6 group-hover:hidden group-focus-visible:hidden"
          aria-hidden="true"
        />
        <IconSidebarToggle
          className="hidden size-5 group-hover:block group-focus-visible:block"
          aria-hidden="true"
        />
      </button>
      {tooltip}
    </>
  )
}

function OpenSidebarToggle({ onToggle }: { onToggle: () => void }) {
  const { t } = useTranslation()
  const { triggerProps, hide, tooltip } = useSidebarTooltip(t('nav.closeMenu'))

  return (
    <>
      <button
        type="button"
        onClick={() => {
          hide()
          onToggle()
        }}
        aria-label={t('nav.closeMenu')}
        className="text-icon hover:bg-sidebar-selected flex shrink-0 cursor-pointer items-center justify-center rounded-xs p-2 transition-colors"
        {...triggerProps}
      >
        <IconSidebarToggle className="size-5" aria-hidden="true" />
      </button>
      {tooltip}
    </>
  )
}

function CollapsedActionItem({
  action,
  onNavigate,
}: {
  action: SidebarAction
  onNavigate?: () => void
}) {
  const { triggerProps, hide, tooltip } = useSidebarTooltip(action.label)
  return (
    <>
      <button
        type="button"
        onClick={() => {
          hide()
          action.onClick()
          onNavigate?.()
        }}
        className={cn(
          'flex w-full cursor-pointer items-center rounded-xs p-2 transition-colors',
          action.isActive
            ? 'bg-sidebar-selected text-sidebar-icon-selected'
            : 'text-sidebar-icon hover:bg-sidebar-selected',
        )}
        {...triggerProps}
      >
        <action.Icon className="size-5 shrink-0" aria-hidden="true" />
        <span className="sr-only">{action.label}</span>
      </button>
      {tooltip}
    </>
  )
}

function CollapsedNavItem({
  item,
  onNavigate,
  forceInactive,
}: {
  item: NavItem
  onNavigate?: () => void
  forceInactive?: boolean
}) {
  const { triggerProps, hide, tooltip } = useSidebarTooltip(item.label)
  const { search } = useLocation()

  const excluded = (item.to === DISCOVER_PATH && hasActiveCategory(search)) || forceInactive

  return (
    <>
      <NavLink
        to={item.to}
        end={item.end ?? false}
        onClick={() => {
          hide()
          onNavigate?.()
        }}
        className={({ isActive }) =>
          cn(
            'flex w-full items-center rounded-xs p-2 transition-colors',
            isActive && !excluded
              ? 'bg-sidebar-selected text-sidebar-icon-selected'
              : 'text-sidebar-icon hover:bg-sidebar-selected',
          )
        }
        {...triggerProps}
      >
        {({ isActive: linkActive }) => {
          const isActive = linkActive && !excluded
          const Glyph = isActive ? (item.ActiveIcon ?? item.Icon) : item.Icon
          return (
            <>
              <Glyph className="size-5" aria-hidden="true" />
              <span className="sr-only">{item.label}</span>
            </>
          )
        }}
      </NavLink>
      {tooltip}
    </>
  )
}

interface SidebarProps {
  variant: 'rail' | 'panel'
  onToggle: () => void
  onExplore: () => void
  exploreOpen?: boolean
  onNavigate?: () => void
  className?: string
}

export function Sidebar({
  variant,
  onToggle,
  onExplore,
  exploreOpen = false,
  onNavigate,
  className,
}: SidebarProps): ReactElement {
  const { t } = useTranslation()
  const localize = useLocalize()
  const open = variant === 'panel'

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})

  const fixMenu: FixEntry[] = [
    {
      kind: 'link',
      item: {
        to: DISCOVER_PATH,
        end: true,
        label: t('nav.home'),
        Icon: IconHomeOutline20,
        ActiveIcon: IconHomeFilled,
      },
    },
    {
      kind: 'action',
      action: {
        label: t('nav.explore'),
        Icon: IconNavSearch,
        onClick: onExplore,
        isActive: exploreOpen,
      },
    },
    {
      kind: 'link',
      item: { to: '/map', label: t('nav.map'), Icon: open ? IconMap20 : IconMap },
    },
  ]

  const sidebarGroups = SIDEBAR_GROUPS.flatMap(({ id, pinned }) => {
    const group = PRODUCT_GROUPS.find((candidate) => candidate.id === id)
    return group ? [{ group, products: orderProducts(group, pinned) }] : []
  })

  return (
    <nav
      data-node-id={open ? '2256:282261' : '2256:281963'}
      data-name="sidebar"
      aria-label={t('nav.primary')}
      className={cn(
        'hub-sidebar flex h-dvh flex-col items-start gap-4 overflow-y-auto p-3',
        open ? 'w-55.5' : 'w-15',
        className,
      )}
    >
      <div
        data-node-id="1019:58863"
        className="flex w-full items-center justify-between rounded-xs"
      >
        {open ? (
          <>
            <div className="text-icon flex size-9 shrink-0 items-center justify-center rounded-[8px] p-2">
              <IconLogoMark className="size-6" aria-hidden="true" />
            </div>

            <OpenSidebarToggle onToggle={onToggle} />
          </>
        ) : (
          <ClosedSidebarToggle onToggle={onToggle} />
        )}
      </div>

      <div
        data-node-id="2869:9843"
        data-name="fix-menu"
        className="flex w-full flex-col items-start"
      >
        {fixMenu.map((entry) => {
          if (entry.kind === 'action') {
            const { action } = entry

            if (!open) {
              return (
                <CollapsedActionItem
                  key={action.label}
                  action={action}
                  {...(onNavigate ? { onNavigate } : {})}
                />
              )
            }

            return (
              <button
                key={action.label}
                type="button"
                onClick={() => {
                  action.onClick()
                  onNavigate?.()
                }}
                className={rowClassName(action.isActive)}
              >
                <action.Icon
                  className={cn(
                    'size-5 shrink-0',
                    action.isActive ? 'text-sidebar-icon-selected' : 'text-sidebar-icon',
                  )}
                  aria-hidden="true"
                />
                <span
                  className={cn(
                    'text-sidebar-text min-w-px flex-1 truncate text-left',
                    action.isActive ? 'hub-label-md-highlight' : 'hub-label-md',
                  )}
                >
                  {action.label}
                </span>
              </button>
            )
          }

          const { item } = entry

          if (open) {
            return (
              <SidebarLink
                key={item.to}
                item={item}
                forceInactive={exploreOpen}
                {...(onNavigate ? { onNavigate } : {})}
              />
            )
          }

          return (
            <CollapsedNavItem
              key={item.to}
              item={item}
              forceInactive={exploreOpen}
              {...(onNavigate ? { onNavigate } : {})}
            />
          )
        })}
      </div>

      {open &&
        sidebarGroups.map(({ group, products }) => {
          const groupOpen = !collapsedGroups[group.id]
          const expanded = !!expandedGroups[group.id]
          const items = expanded ? products : products.slice(0, VISIBLE)

          return (
            <div
              key={group.id}
              data-name={group.id}
              className="flex w-full flex-col items-start justify-center"
            >
              <GroupLabel
                label={localize(group.title)}
                open={groupOpen}
                onToggle={() => {
                  setCollapsedGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))
                }}
              />
              {groupOpen && (
                <div data-name="products" className="flex w-full flex-col items-start">
                  {items.map((product, index) => (
                    <SidebarProductLink
                      key={`${product.id}-${index}`}
                      product={product}
                      forceInactive={exploreOpen}
                      {...(onNavigate ? { onNavigate } : {})}
                    />
                  ))}
                  {products.length > VISIBLE && (
                    <ShowMoreRow
                      expanded={expanded}
                      onToggle={() => {
                        setExpandedGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))
                      }}
                      label={expanded ? t('nav.showLess') : t('nav.showMore')}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}
    </nav>
  )
}
